import { Button, Tooltip } from '@mui/material';

import { ArrowDropDownOutlined } from '@mui/icons-material';
import { tss } from 'tss-react/mui';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useDroppable } from '@dnd-kit/core';

import type { DocumentType } from '@/gql';

import type { OwnerPermisson } from '@/modules/files/hooks/useFilePermissions';
import { useFilePermissions } from '@/modules/files/hooks/useFilePermissions';

import type { FILE_MANAGER_TOOLBAR_BREADCRUMB_FRAGMENT } from '../../FileManagerToolbar';

export type Segment = Readonly<{
  _id: string | null;
  file?: DocumentType<typeof FILE_MANAGER_TOOLBAR_BREADCRUMB_FRAGMENT> | null;
  fileId?: string | null;
  parentId?: string | null;
  name: string | null;
  ownerId: string | null;
  ownerName: string | null;
  ownerType: 'Group' | 'Service';
  folder?: boolean | null;
  hidden?: boolean | null;
  isRootOwner?: boolean | null;
}>;

interface Props {
  segment: Segment;
  ownerPermissions: OwnerPermisson[];
  isLast: boolean | null | undefined;
  openFolderMenu: () => void;
}

const useStyles = tss.create(({ theme }) => ({
  root: {
    minWidth: 0,
  },

  dragOver: {
    backgroundColor: theme.hover,
  },

  segment: {
    padding: theme.spacing(1),
    width: '100%',
    minWidth: 0,
  },

  segmentTooltip: {
    minWidth: 0,
  },

  segmentName: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },

  rightIcon: {
    color: theme.hint,
    marginLeft: theme.spacing(0.5),
  },
}));

export const FileManagerBreadcrumbsBarSegment = ({
  segment,
  ownerPermissions,
  isLast,
  openFolderMenu,
}: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const permission = useFilePermissions(
    segment?.file,
    ownerPermissions,
    segment?.ownerId,
  );

  const { isOver, setNodeRef } = useDroppable({
    id: segment.fileId ?? '',
    data: { file: segment.file, ownerId: segment.ownerId },
    disabled:
      isLast ||
      (!segment.isRootOwner && !(permission.canEdit && segment?.file?.folder)),
  });

  const { classes, cx } = useStyles();

  return (
    <Tooltip
      className={classes.segmentTooltip}
      title={segment.name}
      enterDelay={1000}
    >
      <div className={classes.root}>
        <Button
          className={cx(classes.segment, {
            [classes.dragOver]: isOver,
          })}
          ref={setNodeRef}
          color="info"
          size="large"
          onClick={() => {
            if (isLast) {
              openFolderMenu();
            } else {
              const pathnameWithoutFileId = pathname.substring(
                0,
                pathname.lastIndexOf('/'),
              );

              const pathnameWithNewFileId = `${pathnameWithoutFileId}${
                segment.fileId ? `/${segment.fileId}` : ''
              }`;

              const newPathname = `${pathnameWithNewFileId}${
                searchParams.size ? `?${searchParams.toString()}` : ''
              }`;

              router.push(newPathname);
            }
          }}
        >
          <div className={classes.segmentName}>{segment.name}</div>
          {isLast && Boolean(segment.folder) && (
            <ArrowDropDownOutlined className={classes.rightIcon} />
          )}
        </Button>
      </div>
    </Tooltip>
  );
};
